import React from 'react';
import { useProgressDisplayStore } from '../../stores/progressDisplayStore';
import { useXpReward } from '../../hooks/useXpReward';
import { XpChip } from './XpChip';

interface RailRewardProps {
  /** Pixel size of the rail progress ring this overlays. */
  size: number;
  /** Stroke width of the main (user-level) ring. */
  strokeWidth: number;
  isCollapsed?: boolean;
}

const clamp = (n: number) => Math.max(0, Math.min(100, isNaN(n) ? 0 : n));

/**
 * Desktop rail XP-reward overlay. Rendered inside the (relative) progress-ring
 * button so its pieces anchor to the ring. The ring already shows persistent
 * user progress; this adds the transient "+N XP" chip plus a domain indicator —
 * either a concentric arc inside the ring or a thin line beneath it, per the
 * `desktopDomainStyle` setting. All pieces are pointer-events-none so taps still
 * reach the underlying navigate-to-profile button.
 */
export function RailReward({ size, strokeWidth, isCollapsed = false }: RailRewardProps) {
  const reward = useXpReward();
  const domainStyle = useProgressDisplayStore((s) => s.desktopDomainStyle);
  const color = reward.domainColor || 'rgb(var(--primary))';

  const mainRadius = (size - strokeWidth) / 2;
  const domStroke = isCollapsed ? 2 : 3;
  const domRadius = mainRadius - (strokeWidth + domStroke + 1);
  const circ = 2 * Math.PI * domRadius;
  const offset = circ - (clamp(reward.domPct) / 100) * circ;

  return (
    <>
      <XpChip
        amount={reward.amount}
        token={reward.token}
        color={reward.domainColor}
        className="left-1/2 top-0 -translate-x-1/2 -translate-y-3"
      />

      {domainStyle === 'arc' ? (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
          style={{ opacity: reward.active ? 1 : 0, transition: 'opacity 400ms ease-out' }}
          aria-hidden
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={domRadius}
            fill="none"
            stroke="currentColor"
            strokeWidth={domStroke}
            className="text-border opacity-20"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={domRadius}
            fill="none"
            stroke={color}
            strokeWidth={domStroke}
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="drop-shadow-sm"
          />
        </svg>
      ) : (
        <span
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 overflow-hidden rounded-full bg-muted"
          style={{
            bottom: -8,
            height: 3,
            width: Math.round(size * (isCollapsed ? 0.8 : 0.55)),
            opacity: reward.active ? 1 : 0,
            transition: 'opacity 400ms ease-out',
          }}
          aria-hidden
        >
          <span
            className="block h-full rounded-full"
            style={{ width: `${clamp(reward.domPct)}%`, background: color }}
          />
        </span>
      )}
    </>
  );
}
